/**
 * GrandTotalSummary Component
 * 
 * Renders the grand total summary section for quotations.
 * Automatically hides for multi-option quotes (customer picks ONE option).
 * 
 * Usage:
 *   <GrandTotalSummary
 *     quotation={quotation}
 *     dmcParsed={dmcParsed}
 *     variant="detail"
 *   />
 */

import React from 'react';
import { 
  formatCurrency, 
  shouldHideGrandTotal 
} from './displayConfig';

const GrandTotalSummary = ({ 
  quotation = {},
  dmcParsed = {},
  variant = 'detail',
  paymentPolicy = null,
  className = ''
}) => {
  // Check if we should hide this section
  if (shouldHideGrandTotal(quotation, dmcParsed)) {
    return null;
  }
  
  const miscCosts = quotation.quote_line_items?.misc_costs || quotation.misc_costs || [];
  
  // Get pricing values
  const tourAmount = quotation.net_quoted_price || 0;
  const gstPct = quotation.gst_percentage || 0;
  const gstIncluded = quotation.gst_included !== false;
  
  // Don't show if no meaningful data
  if (tourAmount === 0) return null;
  
  // Calculate Package GST
  let packageGst = 0;
  if (gstPct > 0) {
    if (quotation.gst_apply_on === 'markup') {
      packageGst = (quotation.markup_amount || 0) * (gstPct / 100);
    } else {
      packageGst = tourAmount * (gstPct / 100);
    }
  }
  
  // Get included misc costs only (is_optional = false means INCLUDED)
  const includedMiscCosts = miscCosts.filter(c => !c.is_optional);
  const optionalMiscCosts = miscCosts.filter(c => c.is_optional);
  const miscAmount = includedMiscCosts.reduce((sum, c) => sum + (c.calculated_amount || c.amount || 0), 0);
  const miscGst = includedMiscCosts.reduce((sum, c) => sum + (c.gst_amount || 0), 0);
  const discount = quotation.show_discount_separately ? (quotation.discount_amount || 0) : 0;
  
  // Step-4 fix: `gst_included` alone decides the GST-separately behaviour.
  // Package type (DMC / Predefined / Custom / Manual) does not.
  const showGstSeparately = gstPct > 0 && !gstIncluded;
  
  // Ticket #285275 (Option A): "included" + stored GST > 0 means GST was
  // added to the price (legacy included quotes store 0 and keep net).
  const gstInTotal = gstIncluded && Number(quotation.gst_amount || 0) > 0;
  const addGst = !gstIncluded || gstInTotal;

  // Calculate totals
  const tourTotal = addGst ? (tourAmount + packageGst) : tourAmount;
  const miscTotal = miscAmount + miscGst;
  const grandTotal = addGst
    ? (tourAmount + packageGst + miscAmount + miscGst - discount)
    : (tourAmount + miscAmount + miscGst - discount);
  
  return (
    <div className={`mt-6 border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      <h3 className="font-semibold text-lg px-4 py-3 bg-gray-50 border-b border-gray-200 text-navy">
        Grand Total Summary
      </h3>
      
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b">
            <th className="py-3 px-4 text-left font-semibold text-navy">Description</th>
            <th className="py-3 px-4 text-right font-semibold text-navy">Amount</th>
            {showGstSeparately && (
              <th className="py-3 px-4 text-right font-semibold text-navy">GST ({gstPct}%)</th>
            )}
            <th className="py-3 px-4 text-right font-semibold text-navy">Total</th>
          </tr>
        </thead>
        <tbody>
          {/* Package Cost Row */}
          <tr className="border-b bg-gray-50">
            <td className="py-3 px-4 font-medium">
              Package Cost ({quotation.hotel_category || 'Standard'})
            </td>
            <td className="py-3 px-4 text-right">{formatCurrency(tourAmount)}</td>
            {showGstSeparately && (
              <td className="py-3 px-4 text-right">{formatCurrency(packageGst)}</td>
            )}
            <td className="py-3 px-4 text-right font-semibold">{formatCurrency(tourTotal)}</td>
          </tr>
          
          {/* Miscellaneous Charges Row */}
          {(miscAmount > 0 || includedMiscCosts.length > 0) && (
            <tr className="border-b">
              <td className="py-3 px-4 font-medium">Miscellaneous Charges</td>
              <td className="py-3 px-4 text-right">{miscAmount > 0 ? formatCurrency(miscAmount) : '-'}</td>
              {showGstSeparately && (
                <td className="py-3 px-4 text-right">{miscGst > 0 ? formatCurrency(miscGst) : '-'}</td>
              )}
              <td className="py-3 px-4 text-right font-semibold">{formatCurrency(miscTotal)}</td>
            </tr>
          )}
          
          {/* Discount Row */}
          {discount > 0 && (
            <tr className="border-b bg-[color-mix(in_srgb,var(--cyan)_5%,white)]">
              <td className="py-3 px-4 font-medium text-[var(--navy)]">Discount Applied</td>
              <td className="py-3 px-4 text-right text-[var(--navy)]">-{formatCurrency(discount)}</td>
              {showGstSeparately && <td className="py-3 px-4"></td>}
              <td className="py-3 px-4 text-right font-semibold text-[var(--navy)]">-{formatCurrency(discount)}</td>
            </tr>
          )}
          
          {/* Grand Total Row — the one place a gradient fill is used, per
              UI Fix Brief (2026-02): this is the single most prominent total
              moment on the page. */}
          <tr className="text-white font-bold" style={{ background: 'var(--qt-gradient)' }}>
            <td className="py-4 px-4 text-lg">Grand Total</td>
            <td className="py-4 px-4"></td>
            {showGstSeparately && <td className="py-4 px-4"></td>}
            <td className="py-4 px-4 text-right text-xl">{formatCurrency(grandTotal)}</td>
          </tr>
        </tbody>
      </table>
      
      {/* Optional Items Note */}
      {optionalMiscCosts.length > 0 && (
        <div className="px-4 py-3 bg-[color-mix(in_srgb,var(--cyan)_10%,white)] border-t border-[color-mix(in_srgb,var(--navy)_15%,transparent)]">
          <p className="text-xs text-[var(--navy)] font-medium mb-1">Optional Add-ons (not included above):</p>
          <ul className="text-xs text-[var(--navy)]">
            {optionalMiscCosts.map((cost, idx) => (
              <li key={idx}>
                • {cost.name || cost.description}: {formatCurrency(cost.calculated_amount || cost.amount || 0)}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* GST Included Note — Week-4: single canonical format across wizard,
          saved view, and PDF. "Package Price (GST @ N% included): ₹X" — one
          line, no separate GST row, no contradictory footnotes. */}
      {!showGstSeparately && gstPct > 0 && (
        <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-600" data-testid="gst-included-note">
          {gstInTotal
            ? <>GST @ {gstPct}% included in Total Package cost: {formatCurrency(grandTotal)}</>
            : <>Package Price (GST @ {gstPct}% included): {formatCurrency(grandTotal)}</>}
        </div>
      )}

      {/* NS-1: Payment Schedule rows (same table block per user spec) */}
      {paymentPolicy?.hasPolicy && paymentPolicy.schedule?.length > 0 && grandTotal > 0 && (
        <div
          className="px-0 py-0 border-t border-gray-200"
          data-testid="grand-total-payment-schedule"
        >
          <div className="px-4 py-2 bg-gray-100 text-xs font-semibold text-navy flex justify-between">
            <span>Payment Schedule ({paymentPolicy.policyName})</span>
            {paymentPolicy.isDefault && <span className="text-gray-500 font-normal">Default</span>}
          </div>
          <table className="w-full text-sm">
            <tbody>
              {paymentPolicy.schedule.map((s) => (
                <tr key={s.order} className="border-b last:border-b-0">
                  <td className="py-2 px-4 text-gray-700">
                    <span className="font-medium">{s.order}.</span> {s.label}
                    <span className="text-xs text-gray-500 ml-2">
                      ({s.type === 'balance' ? 'at travel' : `${s.daysBeforeArrival}d before`})
                    </span>
                  </td>
                  <td className="py-2 px-4 text-right text-xs text-gray-500">
                    {s.type === 'percentage' ? `${s.value}%` : s.type === 'balance' ? 'Balance' : 'Fixed'}
                  </td>
                  {showGstSeparately && <td className="py-2 px-4"></td>}
                  <td className="py-2 px-4 text-right font-semibold">{formatCurrency(s.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GrandTotalSummary;
